/**
 * components/shop/CatalogueSearch.jsx
 * The one product search field for a sectioned storefront (restaurant menu,
 * brand catalogue). Controlled: the page owns the query and filters its
 * sections with utils/menuSearch.js. Extracted from BrandShopPage so the
 * restaurant page and any later vertical get the same field, not a fourth
 * hand-rolled copy.
 */
import React from "react";
import styled from "styled-components";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

const Row = styled.div`
  margin-bottom: ${(p) => p.$marginBottom || "1.75rem"};
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  max-width: ${(p) => p.$maxWidth || "480px"};
`;

const Icon = styled(SearchOutlinedIcon)`
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 1.2rem !important;
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.65rem 2.5rem 0.65rem 2.6rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  background: #f8fafc;
  color: #1a1a2e;
  outline: none;
  transition: border-color 0.18s;
  &:focus {
    border-color: ${(p) => p.$accent || "var(--brand-primary, #324d54)"};
    background: #fff;
  }
  &::placeholder {
    color: #94a3b8;
  }
`;

const ClearBtn = styled.button`
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.2rem;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 0 2px;
  &:hover {
    color: #1a1a2e;
  }
`;

const Hint = styled.div`
  margin-top: 6px;
  font-size: 0.85rem;
  color: #64748b;
`;

/**
 * @param {object} p
 * @param {string}   p.value
 * @param {(v: string) => void} p.onChange
 * @param {string}   [p.placeholder]
 * @param {string}   [p.accent]       focus colour (CSS value)
 * @param {string}   [p.maxWidth]
 * @param {string}   [p.marginBottom]
 * @param {string}   [p.hint]         one line under the field (e.g. "12 dishes match")
 * @param {string}   [p.ariaLabel]
 */
export default function CatalogueSearch({
  value,
  onChange,
  placeholder = "Search products…",
  accent,
  maxWidth,
  marginBottom,
  hint,
  ariaLabel = "Search products",
}) {
  return (
    <Row $marginBottom={marginBottom}>
      <InputWrap $maxWidth={maxWidth}>
        <Icon />
        <Input
          type="search"
          inputMode="search"
          autoComplete="off"
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onChange("");
          }}
          $accent={accent}
        />
        {value ? (
          <ClearBtn type="button" aria-label="Clear search" onClick={() => onChange("")}>
            ×
          </ClearBtn>
        ) : null}
      </InputWrap>
      {hint ? <Hint aria-live="polite">{hint}</Hint> : null}
    </Row>
  );
}
